/**
 * Purchase Flow Overlay
 * 
 * A modal overlay for purchasing projects from the marketplace.
 * Supports both credit card (Stripe) and crypto (MetaMask) payments.
 * Shows revenue distribution to creators before purchase.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  X, CreditCard, Wallet, ChevronRight, Check, AlertCircle,
  Loader2, DollarSign, Users, GitFork, Shield, ExternalLink,
  Copy, CheckCircle2, Info, Sparkles
} from 'lucide-react';
import toast from 'react-hot-toast';

import { useAuth } from '../../context/AuthContext';
import { cn } from '../../utils/cn';
import { Button, Badge, Spinner } from '../ui';
import { api } from '../../api';

// ============================================================================
// CONSTANTS
// ============================================================================

const PAYMENT_STEPS = {
  SELECT: 'select',      // Choose payment method
  DETAILS: 'details',    // Enter payment details
  PROCESSING: 'processing',
  SUCCESS: 'success',
  ERROR: 'error'
};

// ============================================================================
// REVENUE SPLIT PREVIEW
// ============================================================================

const RevenueSplitPreview = ({ splits, isLoading }) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner size="sm" />
      </div>
    );
  }
  
  if (!splits || splits.length === 0) {
    return null;
  }
  
  return (
    <div className="bg-zinc-800/30 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <Users className="w-4 h-4 text-purple-400" />
        <span className="text-sm font-medium text-zinc-300">Revenue Distribution</span>
      </div>
      <div className="space-y-2">
        {splits.map((split, idx) => (
          <div 
            key={idx}
            className="flex items-center justify-between text-sm"
          >
            <div className="flex items-center gap-2">
              <div className={cn(
                'w-2 h-2 rounded-full',
                split.type === 'platform' ? 'bg-zinc-500' :
                split.type === 'direct' ? 'bg-emerald-500' : 'bg-purple-500'
              )} />
              <span className="text-zinc-400">
                {split.recipient}
                {split.type === 'royalty' && (
                  <span className="text-zinc-600 ml-1">
                    (from {split.project_name})
                  </span>
                )}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-zinc-500">{split.percentage}%</span>
              <span className={cn(
                'font-medium',
                split.type === 'platform' ? 'text-zinc-400' :
                split.type === 'direct' ? 'text-emerald-400' : 'text-purple-400'
              )}>
                ${split.amount.toFixed(2)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// PAYMENT METHOD SELECTOR
// ============================================================================

const PaymentMethodSelector = ({ selected, onSelect, cryptoEnabled, stripeEnabled }) => {
  return (
    <div className="space-y-3">
      {/* Credit Card Option */}
      <button
        onClick={() => onSelect('stripe')}
        disabled={!stripeEnabled}
        className={cn(
          'w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all',
          'hover:bg-zinc-800/50',
          selected === 'stripe' 
            ? 'border-purple-500 bg-purple-500/10' 
            : 'border-zinc-700/50 bg-zinc-900/50',
          !stripeEnabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <div className={cn(
          'w-12 h-12 rounded-xl flex items-center justify-center',
          'bg-gradient-to-br from-blue-500 to-purple-500'
        )}>
          <CreditCard className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 text-left">
          <div className="font-medium text-white">Credit Card</div>
          <div className="text-sm text-zinc-500">
            {stripeEnabled ? 'Visa, Mastercard, Amex' : 'Coming soon'}
          </div>
        </div>
        {selected === 'stripe' && (
          <Check className="w-5 h-5 text-purple-400" />
        )}
      </button>
      
      {/* Crypto Option */}
      <button
        onClick={() => onSelect('crypto')}
        disabled={!cryptoEnabled}
        className={cn(
          'w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all',
          'hover:bg-zinc-800/50',
          selected === 'crypto'
            ? 'border-purple-500 bg-purple-500/10'
            : 'border-zinc-700/50 bg-zinc-900/50',
          !cryptoEnabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <div className={cn(
          'w-12 h-12 rounded-xl flex items-center justify-center',
          'bg-gradient-to-br from-orange-500 to-amber-500'
        )}>
          <Wallet className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 text-left">
          <div className="font-medium text-white">Crypto (MetaMask)</div>
          <div className="text-sm text-zinc-500">
            {cryptoEnabled ? 'ETH on Ethereum' : 'Coming soon'}
          </div>
        </div>
        {selected === 'crypto' && (
          <Check className="w-5 h-5 text-purple-400" />
        )}
      </button>
    </div>
  );
};

// ============================================================================
// CRYPTO PAYMENT COMPONENT
// ============================================================================

const CryptoPayment = ({ paymentData, onConfirm, onBack }) => {
  const [txHash, setTxHash] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [walletConnected, setWalletConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  
  // Check if MetaMask is installed
  const hasMetaMask = typeof window !== 'undefined' && window.ethereum;
  
  // Connect MetaMask
  const connectWallet = async () => {
    if (!hasMetaMask) {
      toast.error('MetaMask not detected. Please install MetaMask.');
      return;
    }
    
    try {
      const accounts = await window.ethereum.request({ 
        method: 'eth_requestAccounts' 
      });
      setWalletAddress(accounts[0]);
      setWalletConnected(true);
      toast.success('Wallet connected!');
    } catch (err) {
      console.error('Failed to connect wallet:', err);
      toast.error('Failed to connect wallet');
    }
  };
  
  // Send transaction via MetaMask
  const sendTransaction = async () => {
    if (!hasMetaMask || !walletConnected) {
      toast.error('Please connect your wallet first');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Convert amount to wei (ETH has 18 decimals)
      const amountWei = '0x' + Math.floor(paymentData.amount_crypto * 1e18).toString(16);
      
      // Request transaction
      const hash = await window.ethereum.request({
        method: 'eth_sendTransaction',
        params: [{
          from: walletAddress,
          to: paymentData.platform_wallet,
          value: amountWei,
          chainId: '0x' + paymentData.chain_id.toString(16)
        }]
      });
      
      setTxHash(hash);
      toast.success('Transaction sent! Confirming...');
      
      // Confirm with backend
      await onConfirm(hash, paymentData.amount_crypto, paymentData.currency);
      
    } catch (err) {
      console.error('Transaction failed:', err);
      toast.error(err.message || 'Transaction failed');
      setIsSubmitting(false);
    }
  };
  
  // Copy wallet address
  const copyAddress = () => {
    navigator.clipboard.writeText(paymentData.platform_wallet);
    setCopied(true);
    toast.success('Address copied!');
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div className="space-y-6">
      {/* Payment Details */}
      <div className="bg-zinc-800/30 rounded-xl p-4 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-zinc-400">Amount</span>
          <div className="text-right">
            <div className="text-xl font-bold text-white">
              {paymentData.amount_crypto.toFixed(6)} {paymentData.currency}
            </div>
            <div className="text-sm text-zinc-500">
              ≈ ${paymentData.amount_usd.toFixed(2)} USD
            </div>
          </div>
        </div>
        
        <div className="h-px bg-zinc-700/50" />
        
        <div>
          <div className="text-sm text-zinc-400 mb-2">Send to this address:</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-zinc-900 px-3 py-2 rounded-lg text-sm text-zinc-300 font-mono truncate">
              {paymentData.platform_wallet}
            </code>
            <button
              onClick={copyAddress}
              className="p-2 hover:bg-zinc-700 rounded-lg transition-colors"
            >
              {copied ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              ) : (
                <Copy className="w-5 h-5 text-zinc-400" />
              )}
            </button>
          </div>
        </div>
        
        {paymentData.testnet && (
          <div className="flex items-center gap-2 text-amber-400 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>Testnet mode - use test ETH only</span>
          </div>
        )}
      </div>
      
      {/* Wallet Connection */}
      {!walletConnected ? (
        <Button
          onClick={connectWallet}
          className="w-full"
          disabled={!hasMetaMask}
        >
          <Wallet className="w-4 h-4 mr-2" />
          {hasMetaMask ? 'Connect MetaMask' : 'MetaMask Not Installed'}
        </Button>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm text-emerald-400">
            <CheckCircle2 className="w-4 h-4" />
            <span>Connected: {walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}</span>
          </div>
          
          <Button
            onClick={sendTransaction}
            className="w-full"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Processing...' : `Pay ${paymentData.amount_crypto.toFixed(6)} ${paymentData.currency}`}
          </Button>
        </div>
      )}
      
      {/* Manual Transaction Option */}
      <div className="text-center">
        <button
          onClick={() => {
            const hash = prompt('Already sent? Enter transaction hash:');
            if (hash) {
              onConfirm(hash, paymentData.amount_crypto, paymentData.currency);
            }
          }}
          className="text-sm text-purple-400 hover:text-purple-300"
        >
          I already sent the payment
        </button>
      </div>
      
      {/* Back Button */}
      <button
        onClick={onBack}
        className="w-full text-center text-zinc-500 hover:text-zinc-300 text-sm"
      >
        ← Choose different payment method
      </button>
    </div>
  );
};

// ============================================================================
// STRIPE PAYMENT COMPONENT (Placeholder for Stripe Elements)
// ============================================================================
//
// ⚠️ NOT A REAL STRIPE INTEGRATION. handleSubmit below waits 2 seconds and
// calls onConfirm('pi_simulated_' + Date.now()) — a fabricated payment intent
// ID which is then POSTed to /payments/stripe/confirm. The card inputs are
// plain <input> elements; nothing is sent to Stripe and nothing is charged.
//
// Unreachable today: PaymentMethodSelector disables the card option unless
// paymentConfig.stripe.enabled is true, and Stripe keys are not configured on
// Azure. Setting STRIPE_SECRET_KEY / STRIPE_PUBLISHABLE_KEY makes this a LIVE
// path that fakes successful payments.
//
// BEFORE ENABLING STRIPE:
//   1. Replace this component with @stripe/react-stripe-js CardElement and
//      stripe.confirmPayment()
//   2. Ensure the backend verifies every payment_intent_id against the Stripe
//      API and rejects any it cannot confirm — never trust this ID from the
//      client
// ============================================================================

const StripePayment = ({ project, onConfirm, onBack, clientSecret }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  
  // In production, you'd use @stripe/react-stripe-js here
  // This is a placeholder that simulates the flow
  
  const handleSubmit = async () => {
    setIsProcessing(true);
    
    // Simulate payment processing
    // In production, use stripe.confirmPayment()
    setTimeout(() => {
      onConfirm('pi_simulated_' + Date.now());
    }, 2000);
  };
  
  return (
    <div className="space-y-6">
      {/* Card Input Placeholder */}
      <div className="bg-zinc-800/30 rounded-xl p-4 space-y-4">
        <div className="text-sm text-zinc-400 mb-2">Card Details</div>
        
        {/* In production, this would be <CardElement /> from Stripe */}
        <div className="space-y-3">
          <input
            type="text"
            placeholder="Card number"
            className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder:text-zinc-600"
          />
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              placeholder="MM/YY"
              className="bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder:text-zinc-600"
            />
            <input
              type="text"
              placeholder="CVC"
              className="bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder:text-zinc-600"
            />
          </div>
        </div>
        
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <Shield className="w-4 h-4" />
          <span>Secured by Stripe. We never store your card details.</span>
        </div>
      </div>
      
      <Button
        onClick={handleSubmit}
        className="w-full"
        loading={isProcessing}
        disabled={isProcessing}
      >
        {isProcessing ? 'Processing...' : `Pay $${parseFloat(project.price).toFixed(2)}`}
      </Button>
      
      <button
        onClick={onBack}
        className="w-full text-center text-zinc-500 hover:text-zinc-300 text-sm"
      >
        ← Choose different payment method
      </button>
    </div>
  );
};

// ============================================================================
// SUCCESS SCREEN
// ============================================================================

const PurchaseSuccess = ({ project, sale, onClose }) => {
  return (
    <div className="text-center py-8">
      <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
        <CheckCircle2 className="w-10 h-10 text-emerald-400" />
      </div>
      
      <h3 className="text-2xl font-bold text-white mb-2">Purchase Complete!</h3>
      <p className="text-zinc-400 mb-6">
        You now have access to "{project.name}"
      </p>
      
      <div className="bg-zinc-800/30 rounded-xl p-4 mb-6 text-left">
        <div className="flex items-center gap-2 text-sm text-zinc-400 mb-2">
          <Sparkles className="w-4 h-4 text-purple-400" />
          Revenue has been distributed to all creators in the lineage
        </div>
        <div className="text-xs text-zinc-600">
          Thank you for supporting collaborative creativity!
        </div>
      </div>
      
      <div className="flex gap-3">
        <Button onClick={onClose} variant="secondary" className="flex-1">
          Close
        </Button>
        <Button onClick={() => window.location.href = `/dashboard?project=${project.id}`} className="flex-1">
          View Project
        </Button>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN PURCHASE OVERLAY
// ============================================================================

const PurchaseOverlay = ({ project, onClose }) => {
  const { user } = useAuth();
  
  // State
  const [step, setStep] = useState(PAYMENT_STEPS.SELECT);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [revenueSplits, setRevenueSplits] = useState([]);
  const [splitsLoading, setSplitsLoading] = useState(true);
  const [cryptoPaymentData, setCryptoPaymentData] = useState(null);
  const [stripeClientSecret, setStripeClientSecret] = useState(null);
  const [completedSale, setCompletedSale] = useState(null);
  const [error, setError] = useState(null);
  
  // Fetch payment config and revenue splits
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Both public — no auth required. Still routed through the shared
        // client so the base URL comes from apiBase.js rather than being
        // rebuilt here. Per-request .catch keeps one failure from hiding the
        // other's result.
        const [configRes, splitRes] = await Promise.all([
          api.get('/payments/config').catch(() => ({ data: null })),
          api.post('/payments/preview-split', { project_id: project.id })
             .catch(() => ({ data: {} })),
        ]);
        if (configRes.data) setPaymentConfig(configRes.data);
        setRevenueSplits(splitRes.data.splits || []);
      } catch (err) {
        console.error('Error fetching payment data:', err);
      } finally {
        setSplitsLoading(false);
      }
    };
    
    fetchData();
  }, [project.id]);
  
  // Handle payment method selection
  const handleSelectMethod = async (method) => {
    setPaymentMethod(method);
    setStep(PAYMENT_STEPS.DETAILS);
    
    if (method === 'crypto') {
      // Prepare crypto payment
      try {
        const res = await api.post('/payments/crypto/prepare', {
          project_id: project.id,
          currency: 'ETH',
        });
        setCryptoPaymentData(res.data);
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to prepare payment');
        setStep(PAYMENT_STEPS.ERROR);
      }
    } else if (method === 'stripe') {
      // Create Stripe PaymentIntent
      try {
        const res = await api.post('/payments/stripe/create-intent', {
          project_id: project.id,
        });
        setStripeClientSecret(res.data.client_secret);
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to create payment');
        setStep(PAYMENT_STEPS.ERROR);
      }
    }
  };
  
  // Handle crypto payment confirmation
  const handleCryptoConfirm = async (txHash, amountCrypto, currency) => {
    setStep(PAYMENT_STEPS.PROCESSING);
    
    try {
      const res = await api.post('/payments/crypto/confirm', {
        project_id: project.id,
        tx_hash: txHash,
        amount_crypto: amountCrypto,
        currency: currency,
        amount_usd: cryptoPaymentData.amount_usd,
      });
      setCompletedSale(res.data.sale);
      setStep(PAYMENT_STEPS.SUCCESS);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Payment confirmation failed');
      setStep(PAYMENT_STEPS.ERROR);
    }
  };
  
  // Handle Stripe payment confirmation
  const handleStripeConfirm = async (paymentIntentId) => {
    setStep(PAYMENT_STEPS.PROCESSING);
    
    try {
      const res = await api.post('/payments/stripe/confirm', {
        payment_intent_id: paymentIntentId,
      });
      setCompletedSale(res.data.sale);
      setStep(PAYMENT_STEPS.SUCCESS);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Payment confirmation failed');
      setStep(PAYMENT_STEPS.ERROR);
    }
  };
  
  const price = parseFloat(project.price) || 0;
  const isFree = price === 0;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-zinc-900 border-b border-zinc-800 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">
            {step === PAYMENT_STEPS.SUCCESS ? 'Success!' : 'Purchase Project'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6">
          {/* Project Preview */}
          {step !== PAYMENT_STEPS.SUCCESS && (
            <div className="flex items-center gap-4 mb-6 pb-6 border-b border-zinc-800">
              {project.image_url ? (
                <img 
                  src={project.image_url}
                  alt={project.name}
                  className="w-16 h-16 rounded-xl object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-white text-xl font-bold">
                  {project.name?.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-white truncate">{project.name}</h3>
                <p className="text-sm text-zinc-500">
                  by @{project.creator_username || project.author?.username || 'unknown'}
                </p>
              </div>
              <div className="text-right">
                <div className="text-xl font-bold text-emerald-400">
                  {isFree ? 'Free' : `$${price.toFixed(2)}`}
                </div>
              </div>
            </div>
          )}
          
          {/* Step Content */}
          {step === PAYMENT_STEPS.SELECT && (
            <>
              {/* Revenue Split Preview */}
              <div className="mb-6">
                <RevenueSplitPreview splits={revenueSplits} isLoading={splitsLoading} />
              </div>
              
              {/* Payment Method Selection */}
              <div className="mb-4">
                <div className="text-sm font-medium text-zinc-300 mb-3">
                  Choose payment method
                </div>
                <PaymentMethodSelector
                  selected={paymentMethod}
                  onSelect={handleSelectMethod}
                  stripeEnabled={paymentConfig?.stripe?.enabled}
                  cryptoEnabled={paymentConfig?.crypto?.enabled}
                />
              </div>
              
              {/* If neither is enabled */}
              {paymentConfig && !paymentConfig.stripe?.enabled && !paymentConfig.crypto?.enabled && (
                <div className="text-center py-8 text-zinc-500">
                  <AlertCircle className="w-12 h-12 mx-auto mb-3 text-zinc-600" />
                  <p>Payment methods are not yet configured.</p>
                  <p className="text-sm mt-1">Please contact the platform administrator.</p>
                </div>
              )}
            </>
          )}
          
          {step === PAYMENT_STEPS.DETAILS && paymentMethod === 'crypto' && cryptoPaymentData && (
            <CryptoPayment
              paymentData={cryptoPaymentData}
              onConfirm={handleCryptoConfirm}
              onBack={() => setStep(PAYMENT_STEPS.SELECT)}
            />
          )}
          
          {step === PAYMENT_STEPS.DETAILS && paymentMethod === 'stripe' && (
            <StripePayment
              project={project}
              clientSecret={stripeClientSecret}
              onConfirm={handleStripeConfirm}
              onBack={() => setStep(PAYMENT_STEPS.SELECT)}
            />
          )}
          
          {step === PAYMENT_STEPS.PROCESSING && (
            <div className="text-center py-12">
              <Loader2 className="w-12 h-12 text-purple-400 animate-spin mx-auto mb-4" />
              <p className="text-white font-medium">Processing payment...</p>
              <p className="text-sm text-zinc-500 mt-1">Please don't close this window</p>
            </div>
          )}
          
          {step === PAYMENT_STEPS.SUCCESS && (
            <PurchaseSuccess 
              project={project} 
              sale={completedSale} 
              onClose={onClose} 
            />
          )}
          
          {step === PAYMENT_STEPS.ERROR && (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-400" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">Payment Failed</h3>
              <p className="text-zinc-400 mb-6">{error || 'Something went wrong'}</p>
              <Button onClick={() => setStep(PAYMENT_STEPS.SELECT)}>
                Try Again
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PurchaseOverlay;